import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { mergeMap, of } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap } from 'rxjs/operators';
import { IRegion } from '../../models/region.model';
import { RegionApiService } from '../../services/api/region.service';
import { IErrorResult } from '../../services/dto.service';
import { GoogleService } from '../../services/utility/google.service';
import { AppState, IRegionState } from '../app.state';
import { EntityEffects } from '../entity.effects';
import { fromNewspaperActions } from '../newspaper/newspaper.actions';
import { RouterSelector } from '../router/router.selectors';
import { fromRegionActions } from './region.actions';
import { RegionSelector } from './region.selectors';

@Injectable()
export class RegionEffects extends EntityEffects<IRegion, IRegionState> {
	constructor(actions$: Actions, store: Store<AppState>, private regionApiService: RegionApiService, private regionSelector: RegionSelector, private googleService: GoogleService, routerSelector: RouterSelector) {
		super(actions$, store, regionApiService, regionSelector, routerSelector, fromRegionActions);
	}

	public onNameChanged$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromRegionActions.nameChanged),
			debounceTime(500),
			map(({ name }) => {
				if (name != null && name.length >= 3) {
					return fromRegionActions.suggest();
				}

				return fromRegionActions.loaded({ list: [] });
			})
		)
	);

	public onSuggest$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromRegionActions.suggest),
			concatLatestFrom(() => this.store.select(this.regionSelector.name)),
			switchMap(([, name]) =>
				this.regionApiService.suggest({ name }).pipe(
					map(list => fromRegionActions.loaded({ list })),
					catchError((response: IErrorResult) => of(this.entityActions.failed({ message: response.error?.error || response.error?.message })))
				)
			)
		)
	);

	public onLoaded$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromRegionActions.loaded),
			concatLatestFrom(() => this.store.select(this.regionSelector.name)),
			map(([{ list }, name]) => list.find(region => region.name == name)),
			map(entity => fromRegionActions.selected({ entity }))
		)
	);

	public onSelectedForSearch$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromRegionActions.selected),
			map(() => fromNewspaperActions.load())
		)
	);

	public onRegionSelectedForAnalysis$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromRegionActions.selected),
				filter(({ entity }) => entity != null),
				mergeMap(({ entity }) => [this.googleService.changeRegion(entity)])
			),
		{ dispatch: false }
	);
}
